import { useEffect } from 'react';

/*
 * Reveals elements marked with data-reveal as they scroll into view.
 *
 * Two deliberate safety properties:
 *
 * 1. The hidden start state lives behind .reveal-ready on <html>, which only
 *    this hook adds. If the bundle fails or IntersectionObserver is missing,
 *    nothing is ever hidden - important because the prerendered HTML is what
 *    crawlers and first paint show.
 * 2. Anything already on screen when the hook runs is marked revealed before
 *    the hidden state is armed, so visible content never flashes out and back.
 */
export default function useScrollReveal(routeKey) {
  useEffect(() => {
    const targets = Array.from(document.querySelectorAll('[data-reveal]'));
    if (!targets.length) return undefined;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < viewportHeight * 0.9) {
        el.classList.add('is-revealed');
      }
    });

    const root = document.documentElement;
    root.classList.add('reveal-ready');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.06 }
    );

    targets
      .filter((el) => !el.classList.contains('is-revealed'))
      .forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      root.classList.remove('reveal-ready');
    };
    // Re-runs per route so each page's [data-reveal] elements get observed.
  }, [routeKey]);
}
